import { LIMITS } from '@roots/schema';
// Editor tools — App Flow §3: theme pick, bonus prompt, anonymous tracker, handoff.
import { Button, Card, Input, Section, useToast } from '@roots/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.js';

interface CycleLike {
  id: string;
  status: string;
  theme: string | null;
  year_month: string;
  editor_user_id: string | null;
}

export function EditorTools({
  familyId,
  familyName,
  cycle,
  stats,
  isEditor,
}: {
  familyId: string;
  familyName: string;
  cycle: CycleLike;
  stats: { submitted_count: number; total_eligible_members: number };
  isEditor: boolean;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const [custom, setCustom] = useState('');
  const [bonus, setBonus] = useState('');

  const needsTheme = cycle.status === 'awaiting_theme';
  const canHandoff = ['closed', 'compiling', 'dropped'].includes(cycle.status);

  const { data: suggestions, isLoading: loadingThemes } = useQuery({
    queryKey: ['theme-suggestions', cycle.id],
    queryFn: () => api.themeSuggestions(cycle.id),
    enabled: isEditor && needsTheme,
    staleTime: Number.POSITIVE_INFINITY, // one AI call per editor visit (API §7.4)
  });

  const { data: candidates } = useQuery({
    queryKey: ['editor-candidates', familyId],
    queryFn: () => api.editorCandidates(familyId),
    enabled: isEditor && canHandoff,
  });

  const setTheme = useMutation({
    mutationFn: (theme: string) => api.setTheme(cycle.id, theme),
    onSuccess: () => {
      push('Theme set! Prompts are on their way to your family.');
      void queryClient.invalidateQueries({ queryKey: ['cycle', familyId] });
    },
    onError: () => push('Couldn’t save your theme. Try again.', 'error'),
  });

  const addPrompt = useMutation({
    mutationFn: (text: string) => api.addPrompt(cycle.id, text),
    onSuccess: () => {
      push('Bonus prompt sent! Your family will see it now.');
      setBonus('');
      void queryClient.invalidateQueries({ queryKey: ['cycle', familyId] });
    },
    onError: () => push('That’s both bonus prompts for this month.', 'error'),
  });

  const handoff = useMutation({
    mutationFn: (userId: string) => api.handoff(familyId, userId),
    onSuccess: (_data, userId) => {
      const name = candidates?.candidates.find((c) => c.user_id === userId)?.name ?? 'They';
      push(`${name} is next month’s editor! 🎉`);
      void queryClient.invalidateQueries({ queryKey: ['cycle', familyId] });
    },
  });

  if (!isEditor) return null;

  return (
    <Section title="Editor tools">
      {needsTheme && (
        <Card className="flex flex-col gap-3">
          <h3 className="type-heading">Pick this month’s theme</h3>
          {loadingThemes && <p className="type-caption">Finding some ideas…</p>}
          <div className="flex flex-col gap-2">
            {suggestions?.suggestions.map((s) => (
              <button
                key={s.title}
                type="button"
                onClick={() => setTheme.mutate(s.title)}
                className="rounded-[var(--radius-md)] border border-[var(--color-accent-warm-gray)] p-3 text-left"
              >
                <p className="type-display text-[length:var(--type-heading-size)]">{s.title}</p>
                <p className="type-caption mt-1">{s.description}</p>
              </button>
            ))}
          </div>
          <Input
            label="Or write your own"
            value={custom}
            maxLength={LIMITS.themeMaxChars}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="Kitchen Table Stories"
          />
          <Button
            onClick={() => setTheme.mutate(custom)}
            disabled={custom.trim().length === 0}
            loading={setTheme.isPending}
          >
            Confirm theme
          </Button>
        </Card>
      )}

      {!needsTheme && (
        <Card className="flex flex-col gap-3">
          <p className="type-body">
            {stats.submitted_count} of {stats.total_eligible_members} members have shared so far
          </p>
          {cycle.status === 'open' && (
            <>
              <Input
                label="Send a bonus prompt"
                value={bonus}
                maxLength={LIMITS.promptMaxChars}
                onChange={(e) => setBonus(e.target.value)}
                placeholder="Write a bonus prompt for your family (keep it short and inviting)..."
                hint="Good prompts are short, open-ended, and photo-friendly."
              />
              <Button
                variant="secondary"
                onClick={() => addPrompt.mutate(bonus)}
                disabled={bonus.trim().length === 0}
                loading={addPrompt.isPending}
              >
                Send to {familyName}
              </Button>
            </>
          )}
        </Card>
      )}

      {canHandoff && candidates && (
        <Card className="flex flex-col gap-3">
          <h3 className="type-heading">Pick next month’s editor</h3>
          <ul className="flex flex-col gap-2">
            {candidates.candidates.map((candidate) => (
              <li key={candidate.user_id} className="flex items-center justify-between gap-3">
                <span className="type-body">
                  {candidate.name}
                  {candidate.last_edited_year_month ? '' : ' · hasn’t edited yet'}
                </span>
                <Button variant="secondary" onClick={() => handoff.mutate(candidate.user_id)}>
                  Choose
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </Section>
  );
}
